import { type FC } from 'react';
import cx from 'classnames';
import { format } from 'date-fns';
import { Popover, Tag, Tooltip } from 'antd';
import { Title, Text, Space } from '@g2u/ui-kit';
import { InfoCircleFilled, LikeFilled, PhoneFilled, WarningFilled } from '@ant-design/icons';
import { presetPalettes } from '@ant-design/colors';
import he from 'he';

import { parseTime } from 'helpers';
import { FullAvailabilityStatus, type JsonDataItem, type WithOptionalDataTest } from 'types';
import styles from './availability-item.module.scss';

type Props = {
  isBest?: boolean;
  techInfo: JsonDataItem;
  slotInfo: JsonDataItem;
  id: string;
  time: string;
  isConfirmationRequired: boolean;
  isShortNotice?: boolean;
  isActive?: boolean;
  isSurrounding?: boolean;
  isOtherTimezone?: boolean;
  isSecondaryArea?: boolean;
  isDisabled?: boolean;
  hasPopover?: boolean;
  tag: FullAvailabilityStatus | null;
  onClick: (id: string, time: string) => void;
} & WithOptionalDataTest;

export const AvailabilityItem: FC<Props> = (props) => {
  const {
    techInfo,
    slotInfo,
    id,
    time,
    onClick,
    isActive,
    isConfirmationRequired,
    isShortNotice,
    isBest,
    isSurrounding,
    isOtherTimezone,
    isSecondaryArea,
    dataTestId,
    isDisabled,
    hasPopover = true,
    tag,
  } = props;

  const reasonText = [
    isShortNotice && 'Short Notice',
    isSurrounding && 'Surrounding Area',
    isConfirmationRequired && 'Requires Confirmation',
    tag,
  ]
    .filter(Boolean)
    .join(', ');

  const isUnavailableSlot =
    tag &&
    [FullAvailabilityStatus.OnSiteJob, FullAvailabilityStatus.RemoteJob, FullAvailabilityStatus.TravelBuffer].includes(
      tag,
    );

  const tagStyleMap: Record<FullAvailabilityStatus, string> = {
    [FullAvailabilityStatus.Available]: '',
    [FullAvailabilityStatus.RequiresConfirmation]: 'cyan',
    [FullAvailabilityStatus.Unavailable]: '',
    [FullAvailabilityStatus.Away]: 'red',
    [FullAvailabilityStatus.OnSiteJob]: 'green',
    [FullAvailabilityStatus.RemoteJob]: 'geekblue',
    [FullAvailabilityStatus.TravelBuffer]: '',
  };

  const isTravelBuffer = tag === FullAvailabilityStatus.TravelBuffer;

  const isManualOverriding =
    tag &&
    [
      FullAvailabilityStatus.Away,
      FullAvailabilityStatus.RequiresConfirmation,
      FullAvailabilityStatus.Unavailable,
    ].includes(tag);

  return (
    <Popover
      open={!hasPopover ? false : undefined}
      content={
        <Space.$ direction={Space.Direction.Vertical} className={styles.availabilityItem__info} size={Space.Size.Small}>
          <Text.$ strong className={styles.availabilityItem__infoTitle} data-dd-privacy="mask">
            {he.decode(techInfo.name)}
          </Text.$>
          {techInfo.mobile?.[0] && <Text.$ strong>{techInfo.mobile[0]}</Text.$>}
          {techInfo.notes && (
            <Space.$ direction={Space.Direction.Vertical} compact>
              <Text.$ strong>Special notes:</Text.$>
              <Text.$>{techInfo.notes}</Text.$>
            </Space.$>
          )}
          <Space.$ direction={Space.Direction.Vertical} compact>
            <Text.$ strong>Coming from:</Text.$>
            <Text.$>
              {slotInfo.info.from?.suburb} {slotInfo.info.from?.travel_time}
            </Text.$>
            <Text.$>{slotInfo.info.from?.time_gap}</Text.$>
            <Text.$ strong>Going to:</Text.$>
            <Text.$>
              {slotInfo.info.to?.suburb} 🏠 {slotInfo.info.to?.travel_time}
            </Text.$>
            <Text.$>{slotInfo.info.to?.time_gap}</Text.$>
          </Space.$>
        </Space.$>
      }
      trigger="hover"
      placement="right"
    >
      <div
        className={cx(
          styles.availabilityItem,
          isActive && styles.availabilityItem_active,
          isDisabled && styles.availabilityItem_disabled,
        )}
        data-testid={dataTestId}
        onClick={() => {
          !isDisabled && onClick(id, time);
        }}
      >
        {tag && (
          <Tag
            color={tagStyleMap[tag]}
            className={cx(styles.availabilityItem__tag, isTravelBuffer && styles.availabilityItem__tag_travelBuffer)}
          >
            {tag}
          </Tag>
        )}
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
          <Space.$ size={Space.Size.Small} direction={Space.Direction.Horizontal} align={Space.Align.Center}>
            <Title.$
              level={4}
              className={cx(styles.availabilityItem__time, isDisabled && styles.availabilityItem__time_disabled)}
              noMargin
              disabled={isDisabled}
            >
              <span className={styles.availabilityItem__timeHours}>{format(parseTime(time), 'HH:mmaaa')}</span>
            </Title.$>

            <div className={styles.availabilityItem__icons}>
              {/* best */}
              {isBest && (
                <LikeFilled className={styles.availabilityItem__icon} style={{ color: presetPalettes.red[5] }} />
              )}

              {/* info */}
              {techInfo.notes && <InfoCircleFilled className={styles.availabilityItem__icon} />}

              {/* confirmation */}
              {(isConfirmationRequired || isShortNotice || isSurrounding || isManualOverriding) &&
                !isUnavailableSlot && (
                  <Tooltip title={reasonText}>
                    <PhoneFilled
                      style={{ color: presetPalettes.orange[5] }}
                      className={cx(styles.availabilityItem__icon)}
                    />
                  </Tooltip>
                )}

              {/* other timezone */}
              {isOtherTimezone && (
                <WarningFilled
                  style={{ color: presetPalettes.orange[5] }}
                  className={cx(styles.availabilityItem__icon)}
                />
              )}
            </div>
          </Space.$>
          <Space.$ direction={Space.Direction.Vertical} compact>
            <Title.$ level={5} className={styles.availabilityItem__name} disabled={isDisabled} data-dd-privacy="mask">
              {he.decode(techInfo.name)}
            </Title.$>
            <Text.$ className={styles.availabilityItem__area}>
              {isSecondaryArea && !isSurrounding && 'secondary'}
              {isSurrounding && 'surrounding-area'}
            </Text.$>
          </Space.$>
        </Space.$>
      </div>
    </Popover>
  );
};
